import { useState, useEffect } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { GripVertical, Save, RefreshCw, Package, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";


export default function ShopProductOrderTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Fetch products from Shopify
      const shopifyRes = await adminApi.get(`/shopify/products`);
      const shopifyProducts = shopifyRes.data.products;
      
      // Fetch custom order
      const orderRes = await adminApi.get(`/shop-product-order`);
      const customOrder = orderRes.data;
      
      // Create order map
      const orderMap = {};
      customOrder.forEach(item => {
        orderMap[item.handle] = item.order;
      });
      
      // Sort products by custom order, then by availability
      const sortedProducts = [...shopifyProducts].sort((a, b) => {
        const orderA = orderMap[a.handle] ?? 999;
        const orderB = orderMap[b.handle] ?? 999;
        
        if (orderA !== orderB) return orderA - orderB;
        
        // If same order, sort by availability
        const aAllSoldOut = a.variants?.every(v => v.available === false) ?? false;
        const bAllSoldOut = b.variants?.every(v => v.available === false) ?? false;
        
        if (aAllSoldOut && !bAllSoldOut) return 1;
        if (!aAllSoldOut && bAllSoldOut) return -1;
        return 0;
      });
      
      setProducts(sortedProducts);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(index, 0, draggedProduct);
    
    setProducts(newProducts);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const orderData = products.map((product, index) => ({
        handle: product.handle,
        order: index
      }));
      
      await adminApi.put(`/shop-product-order`, { products: orderData });
      toast.success("Orden guardado correctamente");
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error("Error al guardar el orden");
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price, currency = 'MXN') => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency
    }).format(price);
  };

  const isProductSoldOut = (product) => {
    return product.variants?.every(v => v.available === false) ?? false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-gray-400 mr-3" size={24} />
        <span className="text-gray-500">Cargando productos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-black flex items-center gap-2">
            <Package size={24} />
            Orden de Productos
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Arrastra y suelta para reordenar. Los productos agotados se muestran al final automáticamente.
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchProducts}
            disabled={loading}
            className="rounded-full"
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-black hover:bg-gray-800 text-white rounded-full"
          >
            <Save size={16} className="mr-2" />
            {saving ? "Guardando..." : "Guardar Orden"}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-yellow-600" />
          <span className="text-yellow-800 text-sm font-medium">
            Tienes cambios sin guardar. Haz clic en "Guardar Orden" para aplicarlos.
          </span>
        </div>
      )}

      {/* Products List */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
          <div className="col-span-1">#</div>
          <div className="col-span-2">Imagen</div>
          <div className="col-span-5">Producto</div>
          <div className="col-span-2">Precio</div>
          <div className="col-span-2">Estado</div>
        </div>
        
        <div className="divide-y divide-gray-100">
          {products.map((product, index) => {
            const soldOut = isProductSoldOut(product);
            return (
              <div
                key={product.handle}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`grid grid-cols-12 gap-4 px-4 py-3 items-center cursor-move hover:bg-gray-50 transition-colors ${
                  draggedIndex === index ? 'bg-blue-50 border-2 border-blue-300' : ''
                } ${soldOut ? 'opacity-50' : ''}`}
              >
                <div className="col-span-1 flex items-center gap-2">
                  <GripVertical size={16} className="text-gray-400" />
                  <span className="text-gray-500 font-medium">{index + 1}</span>
                </div>
                <div className="col-span-2">
                  <img
                    src={product.images?.[0]?.url || '/placeholder.png'}
                    alt={product.title}
                    className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                  />
                </div>
                <div className="col-span-5">
                  <p className="font-medium text-black truncate">{product.title}</p>
                  <p className="text-xs text-gray-400 truncate">{product.handle}</p>
                </div>
                <div className="col-span-2">
                  <span className="font-bold text-black">
                    {formatPrice(product.price, product.currency)}
                  </span>
                </div>
                <div className="col-span-2">
                  {soldOut ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      Agotado
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Disponible
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-gray-400 text-sm">
        {products.length} productos en total
      </p>
    </div>
  );
}
